import { adjustLightness, mixHex, pickAccessibleText } from './color-utils';
import type { SiteThemePalette } from '@/types/database';

export interface DerivedThemeTokens {
  textOnLightest: string;
  textOnLight: string;
  textOnAccent: string;
  textOnDark: string;
  textOnDarkest: string;
  accentHover: string;
  accentActive: string;
  focusRing: string;
  borderSubtle: string;
  borderStrong: string;
  disabledBackground: string;
  disabledText: string;
  // Fixed, not palette-derived — these need to read as "success/warning/
  // error" regardless of what a unit's own accent color happens to be
  // (which could itself be red or green), the same reasoning most design
  // systems keep semantic status colors off the brand palette.
  success: string;
  warning: string;
  error: string;
}

// The single place every "what color is text/border/focus-ring on top of
// X" question gets answered from a unit's raw 5-color palette — see
// scouting-website-template-design-system.md's "semantic color
// derivation" recommendation. createUnitSiteTheme consumes this instead
// of hardcoding lightest->background/darkest->text the way the first
// pass of this theme system did.
export function deriveThemeTokens(palette: SiteThemePalette): DerivedThemeTokens {
  return {
    textOnLightest: palette.darkest,
    textOnLight: palette.darkest,
    textOnAccent: pickAccessibleText(palette.accent, palette.lightest, palette.darkest),
    textOnDark: palette.lightest,
    textOnDarkest: palette.lightest,
    accentHover: adjustLightness(palette.accent, -0.08),
    accentActive: adjustLightness(palette.accent, -0.14),
    focusRing: palette.accent,
    borderSubtle: mixHex(palette.lightest, palette.darkest, 0.16),
    borderStrong: mixHex(palette.lightest, palette.darkest, 0.4),
    disabledBackground: mixHex(palette.lightest, palette.darkest, 0.08),
    disabledText: mixHex(palette.lightest, palette.darkest, 0.38),
    success: '#2e7d32',
    warning: '#ed6c02',
    error: '#d32f2f',
  };
}
